import { Skeleton, SplitBar } from '@/components/primitives';
import { formatShare } from '@/lib/format';
import { ChartLegend, type ChartLegendItem } from './ChartLegend';
import styles from './ShareBars.module.css';

export interface ShareBarRow {
  key: string;
  label: string;
  value: number;
  /** Parts of the value, each with a colour token. Omit for a single-colour bar. */
  segments?: { value: number; color: string }[];
}

export interface ShareBarsProps {
  rows: ShareBarRow[];
  format: (value: number) => string;
  /** Bar colour when a row has no segments. */
  color?: string;
  /** What a full bar means. Defaults to the largest row; pass 1 when values are fractions. */
  max?: number;
  /** Show each row's share of the rows' total beside its value. */
  showShare?: boolean;
  /** Key for the segment colours. */
  legend?: ChartLegendItem[];
  /** Label column width — narrow for short labels ("Titles"), wide for names. */
  labelWidth?: 'sm' | 'md';
}

/**
 * One labelled bar per row, scaled against `max`, optionally split into
 * coloured parts, with the value (and its share of the whole) at the end.
 */
export function ShareBars({
  rows,
  format,
  color = 'var(--data-1)',
  max,
  showShare = false,
  legend,
  labelWidth = 'md',
}: ShareBarsProps) {
  const top = max ?? (rows.reduce((m, r) => Math.max(m, r.value), 0) || 1);
  const whole = rows.reduce((a, r) => a + r.value, 0) || 1;

  return (
    <>
      <ul className={styles.list} data-label={labelWidth}>
        {rows.map((r) => (
          <li key={r.key} className={styles.row}>
            <span className={styles.label}>{r.label}</span>
            <SplitBar
              size="lg"
              extent={r.value / top}
              segments={
                r.segments && r.value
                  ? r.segments.map((seg) => ({ fraction: seg.value / r.value, color: seg.color }))
                  : [{ fraction: 1, color }]
              }
            />
            <span className={styles.value}>
              {format(r.value)}
              {showShare && <span className={styles.share}>{formatShare(r.value / whole, 0)}</span>}
            </span>
          </li>
        ))}
      </ul>
      {legend && <ChartLegend className={styles.legend} items={legend} />}
    </>
  );
}

/** Placeholder bars in the same rows, tapering like a ranked list. */
export function ShareBarsSkeleton({ rows = 6, labelWidth = 'md' }: { rows?: number; labelWidth?: 'sm' | 'md' }) {
  return (
    <ul className={styles.list} data-label={labelWidth} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <li key={i} className={styles.row}>
          <Skeleton shape="text" width="70%" />
          <Skeleton height={10} width={`${Math.max(20, 100 - i * 13)}%`} />
          <span className={styles.value}>
            <Skeleton shape="text" width={56} />
          </span>
        </li>
      ))}
    </ul>
  );
}
